import { useCallback, useEffect, useMemo, useState } from "react";
import { Export } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * S-Curve tab: fetches its own series, today-snapshot and progress events.
 * Events are rendered as dots on the actual line, with a custom tooltip
 * showing per-task deltas, notes and reporter (P2 refactor + dots feature).
 */
export default function ProjectScurveTab({ projectId, doExport }) {
  const { t } = useLang();
  const [scurve, setScurve] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/scurve`).then((r) => setScurve(r.data)).catch(() => setScurve([]));
    api.get(`/projects/${projectId}/scurve/snapshot`).then((r) => setSnapshot(r.data)).catch(() => setSnapshot(null));
    api.get(`/projects/${projectId}/scurve/events`).then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Merge weekly points + event dates in a single ordered data array.
  // Event rows carry `events` (used by the tooltip and dot renderer) and
  // reuse the actual/planned values (backend-computed for event dates).
  const chartData = useMemo(() => {
    const map = new Map();
    scurve.forEach((r) => map.set(r.date, { date: r.date, planned: r.planned, actual: r.actual }));
    const plannedByDate = (dateStr) => {
      const rows = scurve;
      if (!rows.length) return null;
      if (map.has(dateStr) && map.get(dateStr).planned != null) return map.get(dateStr).planned;
      // linear interpolation between neighbours
      let prev = null;
      let next = null;
      for (const r of rows) {
        if (r.date <= dateStr) prev = r;
        if (r.date >= dateStr && !next) next = r;
      }
      if (prev && next && prev.date !== next.date) {
        const t0 = Date.parse(prev.date);
        const t1 = Date.parse(next.date);
        const tx = Date.parse(dateStr);
        const ratio = (tx - t0) / Math.max(t1 - t0, 1);
        return +(prev.planned + (next.planned - prev.planned) * ratio).toFixed(1);
      }
      return prev?.planned ?? next?.planned ?? null;
    };
    events.forEach((ev) => {
      const row = map.get(ev.date) || { date: ev.date, planned: plannedByDate(ev.date), actual: null };
      row.actual = ev.actual;
      row.events = ev.events;
      map.set(ev.date, row);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [scurve, events]);

  return (
    <div className="space-y-4">
      {snapshot && (
        <div data-testid="scurve-snapshot" className={`border rounded-lg p-5 ${
          snapshot.status === "behind" ? "bg-red-50 border-red-200" :
          snapshot.status === "ahead" ? "bg-emerald-50 border-emerald-200" :
          "bg-blue-50 border-blue-200"
        }`}>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="overline-label">{t("expectedToday")}</p>
              <p className="font-heading font-black text-3xl mt-0.5" data-testid="expected-today">{snapshot.expected_pct}%</p>
            </div>
            <div>
              <p className="overline-label">{t("reportedToday")}</p>
              <p className="font-heading font-black text-3xl mt-0.5" data-testid="reported-today">{snapshot.reported_pct}%</p>
            </div>
            <div>
              <p className="overline-label">{t("variance")}</p>
              <p data-testid="scurve-variance" className={`font-heading font-black text-3xl mt-0.5 ${
                snapshot.status === "behind" ? "text-red-600" :
                snapshot.status === "ahead" ? "text-emerald-600" : "text-blue-900"
              }`}>
                {snapshot.variance_pct > 0 ? "+" : ""}{snapshot.variance_pct}%
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="overline-label">{t("elapsed")}</p>
              <p className="text-sm font-mono text-slate-700 mt-1" data-testid="scurve-days">
                {snapshot.days_elapsed} / {snapshot.days_total} {t("days")}
              </p>
              <span data-testid="scurve-status-badge" className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                snapshot.status === "behind" ? "bg-red-600 text-white" :
                snapshot.status === "ahead" ? "bg-emerald-600 text-white" :
                "bg-blue-900 text-white"
              }`}>
                {t(`scurveStatus_${snapshot.status}`)}
              </span>
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-3">
            {snapshot.status === "behind" && t("scurveHintBehind").replace("{v}", Math.abs(snapshot.variance_pct))}
            {snapshot.status === "ahead" && t("scurveHintAhead").replace("{v}", snapshot.variance_pct)}
            {snapshot.status === "on_track" && t("scurveHintOnTrack")}
          </p>
          {(snapshot.spi != null || snapshot.cpi != null) && (
            <div className="mt-4 pt-3 border-t border-slate-300/60 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="scurve-evm">
              <EvmMetric label="SPI" value={snapshot.spi} hint="Schedule Performance Index · EV/PV" testid="evm-spi" />
              <EvmMetric label="CPI" value={snapshot.cpi} hint="Cost Performance Index · EV/AC" testid="evm-cpi" />
              <div className="text-xs">
                <p className="overline-label">EV · Earned Value</p>
                <p className="font-mono font-bold text-slate-900 mt-0.5">${(snapshot.earned_value || 0).toLocaleString()}</p>
                <p className="text-[10px] text-slate-500">Valor ganado</p>
              </div>
              <div className="text-xs">
                <p className="overline-label">AC · Actual Cost</p>
                <p className="font-mono font-bold text-slate-900 mt-0.5">${(snapshot.actual_cost || 0).toLocaleString()}</p>
                <p className="text-[10px] text-slate-500">Costo real acumulado</p>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="scurve-chart">
        <div className="flex items-center justify-between mb-4">
          <p className="overline-label">{t("sCurve")} — {t("progress")}</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-scurve-tab-button" className="gap-2"><Export size={14} weight="bold" /> {t("export")}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white" align="end">
              <DropdownMenuItem data-testid="export-scurve-tab-pdf-portrait" onClick={() => doExport("scurve", "pdf", "portrait")}>PDF ({t("orientPortrait")})</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-scurve-tab-pdf-landscape" onClick={() => doExport("scurve", "pdf", "landscape")}>PDF ({t("orientLandscape")})</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-scurve-tab-xlsx" onClick={() => doExport("scurve", "xlsx")}>Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <ChartTooltip content={<ScurveTooltip t={t} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="planned" name={t("planned")} stroke="#1E3A8A" strokeWidth={2.5} dot={false} />
              <Line
                type="monotone"
                dataKey="actual"
                name={t("actual")}
                stroke="#F59E0B"
                strokeWidth={2.5}
                dot={<EventDot />}
                activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-400 py-16 text-center">{t("noTasks")}</p>
        )}
      </div>
    </div>
  );
}

/** Show a highlighted dot only when this data-point has attached events. */
function EventDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || !payload?.events?.length) return null;
  return (
    <g data-testid={`scurve-dot-${payload.date}`}>
      <circle cx={cx} cy={cy} r={5} fill="#F59E0B" stroke="#fff" strokeWidth={2} />
      <circle cx={cx} cy={cy} r={9} fill="#F59E0B" opacity={0.18} />
    </g>
  );
}

/** SPI / CPI card: green >=1 (good), amber 0.9-1, red <0.9 */
function EvmMetric({ label, value, hint, testid }) {
  if (value == null) {
    return (
      <div className="text-xs" data-testid={testid}>
        <p className="overline-label">{label}</p>
        <p className="font-mono font-bold text-slate-400 mt-0.5">—</p>
        <p className="text-[10px] text-slate-500">{hint}</p>
      </div>
    );
  }
  const tone = value >= 1
    ? "text-emerald-600"
    : value >= 0.9
    ? "text-amber-600"
    : "text-red-600";
  const status = value >= 1 ? "Bueno" : value >= 0.9 ? "Atención" : "Crítico";
  return (
    <div className="text-xs" data-testid={testid}>
      <p className="overline-label">{label}</p>
      <p className={`font-heading font-black text-2xl mt-0.5 ${tone}`}>{value}</p>
      <p className={`text-[10px] font-bold uppercase ${tone}`}>{status}</p>
      <p className="text-[10px] text-slate-500">{hint}</p>
    </div>
  );
}

function ScurveTooltip({ active, payload, t }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const evs = row.events || [];
  return (
    <div
      data-testid={evs.length ? `scurve-tooltip-events-${row.date}` : "scurve-tooltip-simple"}
      className="bg-white border border-slate-200 rounded-md p-2.5 shadow-lg text-xs max-w-sm"
    >
      <p className="font-mono font-bold text-slate-900 mb-1">{row.date}</p>
      {payload.map((p) =>
        p.value == null ? null : (
          <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
            {p.name}: {p.value}%
          </p>
        )
      )}
      {evs.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
          <p className="overline-label">{t("progressEvents")} ({evs.length})</p>
          {evs.map((ev, i) => (
            <div key={i} className="text-slate-700 leading-snug">
              <p className="font-semibold text-slate-900 truncate">{ev.task_name}</p>
              <p>
                {typeof ev.delta === "number" ? (
                  <span className={ev.delta > 0 ? "text-emerald-700 font-bold" : ev.delta < 0 ? "text-red-600 font-bold" : "text-slate-500 font-bold"}>
                    {ev.delta > 0 ? "+" : ""}{ev.delta}%
                  </span>
                ) : (
                  <span className="text-slate-500 font-bold">{ev.progress}%</span>
                )}
                {typeof ev.previous_progress === "number" && (
                  <span className="text-slate-400"> ({ev.previous_progress}% → {ev.progress}%)</span>
                )}
              </p>
              {ev.note && <p className="italic text-slate-500 text-[11px]">“{ev.note}”</p>}
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {ev.updated_by} · {(ev.updated_at || "").slice(0, 10)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
